/**
 * Company API Utility Functions
 *
 * Utilities for sanitizing and normalizing company data
 */

import { sanitizeString } from "../../_shared/utils";
import type { Company, CompanyContactInput, Contact } from "./company.types";

/**
 * Normalizes an array of contact inputs by:
 * 1. Sanitizing all string fields
 * 2. Filtering out contacts where all fields are empty
 * 3. Removing the id field (handled by backend)
 *
 * @param contacts - Array of contact inputs to normalize
 * @returns Array of normalized contacts with at least one non-empty field
 */
export const normalizeContacts = (contacts?: CompanyContactInput[]): Partial<Contact>[] => {
  if (!contacts) return [];

  return contacts
    .map((contact) => {
      if (!contact) return null;

      const { id: _ignored, name, email, fone, whatsapp = null } = contact;
      const normalized: Partial<Contact> = {};

      const sanitizedName = sanitizeString(name);
      if (sanitizedName) normalized.name = sanitizedName;

      const sanitizedEmail = sanitizeString(email);
      if (sanitizedEmail) normalized.email = sanitizedEmail;

      const sanitizedFone = sanitizeString(fone);
      if (sanitizedFone) normalized.fone = sanitizedFone;

      const sanitizedWhatsapp = sanitizeString(whatsapp);
      if (sanitizedWhatsapp) normalized.whatsapp = sanitizedWhatsapp;

      return Object.keys(normalized).length > 0 ? normalized : null;
    })
    .filter((contact): contact is Partial<Contact> => contact !== null);
};

/**
 * Normalizes a company payload by:
 * 1. Sanitizing all string fields
 * 2. Removing undefined/null values
 * 3. Normalizing the contacts array
 * 4. Excluding the id field (handled separately)
 *
 * @param payload - The company payload to normalize
 * @returns Normalized payload ready for API submission
 */
export const normalizeCompanyPayload = (
  payload:
    | Modifiers.Modify<
        Partial<Company>,
        {
          name?: string | null;
          website?: string | null;
          number?: string | null;
          complement?: string | null;
          district?: string | null;
          contact?: CompanyContactInput[];
        }
      >
    | undefined
): Record<string, unknown> & {
  contact: Partial<Contact>[];
} => {
  const body: Record<string, unknown> = {};

  if (payload) {
    for (const [key, value] of Object.entries(payload)) {
      // Skip contact and id - handled separately
      if (key === "contact" || key === "id") continue;

      // Skip undefined/null values
      if (value === undefined || value === null) continue;

      // Sanitize string values
      if (typeof value === "string") {
        const sanitized = sanitizeString(value);

        if (!sanitized) continue;

        body[key] = sanitized;
        continue;
      }

      body[key] = value;
    }
  }

  const normalizedContacts = normalizeContacts(payload?.contact);

  body.contact = normalizedContacts;

  return body as Record<string, unknown> & {
    contact: Partial<Contact>[];
  };
};
